import { useEffect, useRef, useState } from 'react';
import { Box, Card, Group, Text, ThemeIcon, ActionIcon, Button } from '@mantine/core';
import { Clock, Target, X, ArrowRight } from 'react-feather';
import {
  nudgeCategory,
  nudgePersistsAsToast,
  type NudgeCategory,
  type SessionNudge,
} from '../../../../hooks/useSessionNudges';

/**
 * MED-872 / 2.3b AC5 — bottom-left AI nudge toasts.
 *
 * A new nudge surfaces here regardless of whether the AI Insights panel is
 * open. Pacing toasts persist until the coach dismisses them (X); all other
 * categories auto-dismiss after a few seconds. Either way — explicit X or
 * timer auto-dismiss — only the transient toast is hidden; the nudge always
 * remains in the AI Insights panel's history (MED-1150). The nudge is removed
 * from the panel itself only via the panel row's own X, which is wired to a
 * separate `dismissNudge` action on the parent page.
 *
 * Built as a bespoke fixed layer rather than reusing Mantine notifications
 * (which are top-right): the visual language matches the AI Insights cards and
 * the persistence rules differ by nudge type.
 */

const AUTO_DISMISS_MS = 6000;

const CATEGORY_META: Record<
  NudgeCategory,
  { label: string; color: string; borderVar: string }
> = {
  live: { label: 'LIVE', color: 'surveyPurple', borderVar: 'var(--mantine-color-surveyPurple-8)' },
  pace: { label: 'PACE', color: 'warningAmber', borderVar: 'var(--mantine-color-warningAmber-6)' },
  pre_work: { label: 'PRE-WORK', color: 'blue', borderVar: 'var(--mantine-color-blue-6)' },
};

function CategoryIcon({ category }: { category: NudgeCategory }) {
  if (category === 'pace') return <Clock size={12} />;
  if (category === 'pre_work') return <Target size={12} />;
  return <span aria-hidden="true">✨</span>;
}

export interface SessionNudgeToastsProps {
  nudges: SessionNudge[];
  /**
   * MED-1246 — the leader's current section index. When it changes (forward
   * "Next", back, or jump), every visible pacing toast is auto-dismissed (AC4).
   * Null before the session state has loaded.
   */
  currentSectionIndex: number | null;
  /** LIVE CTA (when a suggested prompt exists) — open the AI Insights panel. */
  onViewInInsights?: () => void;
}

export function SessionNudgeToasts({
  nudges,
  currentSectionIndex,
  onViewInInsights,
}: SessionNudgeToastsProps) {
  // Toasts hidden locally — by the auto-dismiss timer OR the coach clicking the
  // toast's X. Neither marks the nudge `dismissed`, so it stays in the AI
  // Insights panel history until removed from the panel itself (MED-1150).
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const scheduled = useRef<Set<string>>(new Set());

  // Schedule auto-dismiss for each newly-seen, non-persistent nudge.
  useEffect(() => {
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    for (const nudge of nudges) {
      if (nudge.dismissed) continue;
      if (scheduled.current.has(nudge.id)) continue;
      if (nudgePersistsAsToast(nudge)) {
        scheduled.current.add(nudge.id); // pacing: never auto-hide
        continue;
      }
      scheduled.current.add(nudge.id);
      const t = setTimeout(() => {
        setHidden(prev => new Set(prev).add(nudge.id));
      }, AUTO_DISMISS_MS);
      timers.push(t);
    }
    return () => timers.forEach(clearTimeout);
  }, [nudges]);

  // MED-1246 AC4 — any current-section change (forward "Next", back, or jump)
  // auto-dismisses every visible pacing toast. This reuses the toast's own
  // local-hide path (the X), NOT `onDismiss`, so the pacing entry stays in the
  // AI Insights panel history untouched (AC5). Keyed off the section index here
  // in the shared toast layer, so it fires for coach and peer-leader alike no
  // matter which control changed the section (AC7).
  const prevSectionIndex = useRef<number | null>(currentSectionIndex);
  useEffect(() => {
    if (prevSectionIndex.current === currentSectionIndex) return;
    prevSectionIndex.current = currentSectionIndex;
    setHidden(prev => {
      const next = new Set(prev);
      for (const nudge of nudges) {
        if (nudge.dismissed) continue;
        if (nudgeCategory(nudge) === 'pace') next.add(nudge.id);
      }
      return next;
    });
  }, [currentSectionIndex, nudges]);

  const visible = nudges.filter(n => !n.dismissed && !hidden.has(n.id)).slice(0, 4);

  if (visible.length === 0) return null;

  return (
    <Box
      data-testid="active-session-nudge-toasts"
      style={{
        position: 'fixed',
        bottom: 24,
        left: 24,
        zIndex: 350,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        maxWidth: 380,
        pointerEvents: 'none',
      }}
    >
      {visible.map(nudge => {
        const category = nudgeCategory(nudge);
        const meta = CATEGORY_META[category];
        return (
          <Card
            key={nudge.id}
            withBorder
            padding="sm"
            radius="md"
            shadow="md"
            data-testid="active-session-nudge-toast"
            data-category={category}
            style={{
              pointerEvents: 'auto',
              borderLeft: `4px solid ${meta.borderVar}`,
              background: category === 'pace' ? 'rgba(255, 248, 220, 0.95)' : 'white',
              animation: 'medleyNudgeToastIn 220ms ease',
            }}
          >
            <Group justify="space-between" align="flex-start" wrap="nowrap" mb={4}>
              <Group gap={6} align="center">
                <ThemeIcon variant="transparent" color={meta.color} size="sm">
                  <CategoryIcon category={category} />
                </ThemeIcon>
                <Text
                  size="xs"
                  fw={700}
                  c={meta.color}
                  tt="uppercase"
                  style={{ letterSpacing: '0.3px' }}
                >
                  {meta.label}
                </Text>
              </Group>
              <ActionIcon
                size="sm"
                variant="subtle"
                color="gray"
                aria-label="Dismiss nudge"
                data-testid="active-session-nudge-toast-dismiss"
                onClick={() => setHidden(prev => new Set(prev).add(nudge.id))}
              >
                <X size={14} />
              </ActionIcon>
            </Group>

            <Text size="sm" lh={1.5}>
              {nudge.message}
            </Text>

            {category === 'live' && nudge.source && onViewInInsights && (
              <Button
                size="compact-xs"
                variant="subtle"
                color="surveyPurple"
                mt={6}
                rightSection={<ArrowRight size={12} />}
                onClick={onViewInInsights}
              >
                View suggested prompt
              </Button>
            )}
          </Card>
        );
      })}
    </Box>
  );
}
